import { toast } from 'sonner';
import { useAssetsStore } from '@/store/assets.store';
import { useTenantStore } from '@/store/tenant.store';
import { useReferenceStore } from '@/store/reference.store.js';
import i18next from 'i18next';
import { buildOwnerGroupedOptions } from '@/lib/party-owner-dropdown.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';
import {
    ASSET_LIST_PICKER_QUERY,
    mapColumnFiltersForQuery,
    readAssetPaginationTotal,
    splitAssetPickerRows,
} from './asset-query.helpers.js';
import { downloadBlob } from '@/lib/download-blob.js';
import { Logger } from '@/lib/app-log.js';

const ASSET_DROPDOWN_TTL_MS = 60_000;
const ASSET_DROPDOWN_REF_ENTITIES = Object.freeze([
    'assetCategory',
    'classification',
    'assetGroup',
    'user',
    'team',
    'unit',
    'role',
]);

/**
 * Backend error code ? i18n key. Keeps API responses language-neutral while the
 * user-facing toast text stays in the locale files (architecture: user-facing
 * Turkish via i18n, not raw backend strings).
 */
const ASSET_ERROR_CODE_I18N = {
    ASSET_EDIT_LOCKED: 'assets:messages.editLocked',
    ASSET_PENDING_OPERATION: 'assets:messages.pendingOperation',
};


export class AssetsIntegration {
    constructor(service) {
        this.service = service;
        this._assetDropdownInflight = null;
        this._assetDropdownLastSuccessAt = 0;
    }

    /**
     * After a create / update / delete the picker rows (`dropdownData.assets`) are stale.
     * Nothing is fetched here: the next `warmForm` / `fetchDropdownData()` reloads them.
     * List, widgets and workflow state are refreshed by the page via `afterMutation()`.
     */
    _invalidateDropdownCache() {
        this._assetDropdownLastSuccessAt = 0;
    }

    _isDropdownCacheWarm(dropdownData, tenantId) {
        if (!dropdownData || dropdownData._tenantId !== tenantId) return false;
        if (!Array.isArray(dropdownData.assets)) return false;
        if (!Array.isArray(dropdownData.locationAssets)) return false;
        if (!Array.isArray(dropdownData.infrastructureAssets)) return false;
        if (!Array.isArray(dropdownData.assetInventoryCategories)) return false;
        if (!Array.isArray(dropdownData.classificationLevels)) return false;
        const owners = dropdownData?.owners;
        if (!Array.isArray(owners) || owners.length !== 4) return false;
        return Date.now() - this._assetDropdownLastSuccessAt < ASSET_DROPDOWN_TTL_MS;
    }

    _getErrorMessage(error) {
        const data = error?.response?.data;
        const code = data?.code || error?.code;
        if (code && ASSET_ERROR_CODE_I18N[code]) {
            return i18next.t(ASSET_ERROR_CODE_I18N[code], { defaultValue: data?.error || error?.message || 'An error occurred' });
        }
        return getIntegrationErrorMessage(error);
    }

    async _ensureDropdownReferenceEntities(tenantId, requiredEntities = ASSET_DROPDOWN_REF_ENTITIES) {
        const { hydrationManager } = await import('@/lib/services/reference-hydration.js');
        let refState = useReferenceStore.getState();
        if (tenantId && !refState.lastHydratedAt) {
            await hydrationManager.hydrateAll();
            refState = useReferenceStore.getState();
        }

        const missingEntities = requiredEntities.filter(
            (entity) => tenantId && !refState.hydratedEntities?.[entity],
        );
        if (missingEntities.length > 0) {
            await Promise.all(missingEntities.map((entity) => hydrationManager.ensureEntity(entity)));
            refState = useReferenceStore.getState();
        }

        const emptyEntities = requiredEntities.filter(
            (entity) => tenantId && (refState.entities?.[entity] || []).length === 0,
        );
        if (emptyEntities.length > 0) {
            await hydrationManager.hydrateAll({ force: true, bulkEntities: emptyEntities });
            refState = useReferenceStore.getState();
        }

        return refState;
    }

    _mapClassificationLevels(rows = []) {
        return (Array.isArray(rows) ? rows : [])
            .filter((level) => level?.name || level?.label)
            .sort((a, b) => {
                const aLevel = Number(a.level);
                const bLevel = Number(b.level);
                if (Number.isFinite(aLevel) && Number.isFinite(bLevel)) {
                    return aLevel - bLevel;
                }
                const aName = String(a.name || a.label || '');
                const bName = String(b.name || b.label || '');
                return aName.localeCompare(bName, 'tr');
            })
            .map((level) => ({
                _id: level._id || level.value,
                id: level._id || level.id || level.value,
                name: level.name || level.label,
                label: level.label || level.name,
                level: level.level,
            }));
    }

    /** Reference cache only (bulk / lazy hydrate); no `getAll(limit=1000)` fallback. */
    async _resolveReferenceDropdownRows(entityKey) {
        const { fetchDropdownOptionsFromReference } = await import('@/lib/reference-dropdown.helper.js');
        const result = await fetchDropdownOptionsFromReference(entityKey);
        return Array.isArray(result?.data) ? result.data : [];
    }

    _dropdownPickerDataReady(dropdownData) {
        const refState = useReferenceStore.getState();
        const hasClassification = (dropdownData?.classificationLevels || []).length > 0;
        const hasAssetGroups = (dropdownData?.assetGroups || []).length > 0
            || Boolean(refState.hydratedEntities?.assetGroup);
        const hasOwnerOptions = (dropdownData?.owners || []).some(
            (g) => Array.isArray(g?.options) && g.options.length > 0,
        );
        return hasClassification && hasAssetGroups && hasOwnerOptions;
    }

    /**
     * Coerce API statistics to plain numbers and plain objects (Chart.js / table).
     * Handles occasional double-wrapped `{ data: { ... } }` payloads.
     */
    _normalizeAssetStatsPayload(raw) {
        if (!raw || typeof raw !== 'object') return null;
        let s = raw;
        if (s.data && typeof s.data === 'object' && 'totalAssets' in s.data) {
            s = s.data;
        }
        const num = (v) => {
            const n = Number(v);
            return Number.isFinite(n) ? n : 0;
        };
        const mapBucket = (b) => {
            if (!b || typeof b !== 'object' || Array.isArray(b)) return {};
            return Object.fromEntries(
                Object.entries(b).map(([k, v]) => {
                    const c = Number(v);
                    return [k, Number.isFinite(c) ? c : 0];
                })
            );
        };
        return {
            ...s,
            totalAssets: num(s.totalAssets),
            activeAssets: num(s.activeAssets),
            inactiveAssets: num(s.inactiveAssets),
            institutionApproved: num(s.institutionApproved),
            institutionNotApproved: num(s.institutionNotApproved),
            totalCurrentValue: num(s.totalCurrentValue),
            byCategory: mapBucket(s.byCategory),
            byHosting: mapBucket(s.byHosting),
        };
    }

    /**
     * List assets without mutating the global assets store (e.g. maintenance registry page).
     */
    async listAssetsQuiet(params = {}) {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId) {
            return { success: false, error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }), data: [], pagination: {} };
        }
        try {
            const queryParams = {
                page: params.page ?? 1,
                limit: params.pageSize ?? 20,
                pageSize: params.pageSize ?? 20,
                search: params.search ?? '',
                sortBy: params.sortBy ?? 'supportEndDate',
                sortOrder: params.sortOrder ?? 'asc',
                tenantId,
                ...mapColumnFiltersForQuery(params.columnFilters),
            };
            if (import.meta.env.DEV) {
                queryParams.debugAssetVisibility = '1';
            }
            if (params.maintenanceFocus) {
                queryParams.maintenanceFocus = params.maintenanceFocus;
            }
            if (params.excludeIds) {
                queryParams.excludeIds = params.excludeIds;
            }
            const response = await this.service.getAll(queryParams);
            if (import.meta.env.DEV) {
                Logger.debug('[SGRC][assets] listAssetsQuiet', {
                    assetVisibilityMeta: response.assetVisibilityMeta,
                    rowCount: (response.data || []).length,
                    total: (response.meta || response.pagination || {}).total,
                });
            }
            const paginationData = response.meta || response.pagination || {};
            const currentPage = parseInt(paginationData.page || paginationData.currentPage || 1, 10);
            const itemsPerPage = parseInt(paginationData.limit || paginationData.pageSize || queryParams.pageSize, 10);
            const totalItems = readAssetPaginationTotal(paginationData);
            const totalPages = parseInt(
                paginationData.pages || paginationData.totalPages || Math.ceil(totalItems / itemsPerPage) || 1,
                10,
            );
            return {
                success: true,
                data: response.data || [],
                pagination: {
                    currentPage,
                    totalPages,
                    totalItems,
                    itemsPerPage,
                },
                uniqueValues: response.uniqueValues,
            };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage, data: [], pagination: {} };
        }
    }

    async countAssetsQuiet(params = {}) {
        const result = await this.listAssetsQuiet({ ...params, page: 1, pageSize: 1 });
        return result.success ? result.pagination?.totalItems ?? null : null;
    }

    /**
     * Reverse links for asset form / detail (vendors, KVKK kay?tlar?, tenant kontrolleri).
     */
    async fetchInventoryLinks(assetId) {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId || !assetId) {
            return { success: false, data: null };
        }
        try {
            const response = await this.service.getInventoryLinks(assetId);
            return { success: response.success !== false, data: response.data };
        } catch (error) {
            return { success: false, data: null, error: this._getErrorMessage(error) };
        }
    }

    async fetchAssetParty(assetId) {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId || !assetId) {
            return { success: false, data: null, partyTemplate: null };
        }
        try {
            const response = await this.service.getParty(assetId);
            return {
                success: response.success !== false,
                data: response.data,
                partyTemplate: response.partyTemplate ?? null,
            };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, data: null, partyTemplate: null, error: errorMessage };
        }
    }

    async fetchAssetPartyTemplate(params = {}) {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId) {
            return { success: false, data: null };
        }
        try {
            const response = await this.service.getPartyTemplate(params);
            return { success: response.success !== false, data: response.data ?? null };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, data: null, error: errorMessage };
        }
    }

    async fetchWorkflowTemplateOptions(params = { includeInactive: true }) {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId) {
            return { success: false, data: [] };
        }
        try {
            const response = await this.service.getWorkflowTemplateOptions(params);
            return { success: response.success !== false, data: response.data || [] };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, data: [], error: errorMessage };
        }
    }

    /**
     * @param {string} assetId
     * @param {object} payload
     * @param {{ silentSuccess?: boolean; deferApply?: boolean }} [options]
     */
    async syncAssetParty(assetId, payload, options = {}) {
        const { silentSuccess = false, deferApply = false } = options;
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId || !assetId) {
            return { success: false, error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }
        try {
            const body = deferApply ? { ...payload, deferApply: true } : payload;
            const response = await this.service.syncParty(assetId, body);
            if (!silentSuccess) {
                const deferred = response.deferred === true;
                toast.success(
                    (deferred
                            ? i18next.t('assets:ownership.deferSuccess', {
                                  defaultValue: 'Ownership change submitted to approval workflow',
                              })
                            : i18next.t('assets:ownership.saveSuccess', {
                                  defaultValue: 'Ownership and roles saved',
                              })),
                );
            }
            return { success: true, data: response.data, deferred: response.deferred === true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        }
    }

    /** Aggregated dependency summary (inventory links + license / disposal / lifecycle excerpts). */
    async fetchAssetDependencySummary(assetId) {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId || !assetId) {
            return { success: false, data: null };
        }
        try {
            const response = await this.service.getAssetDependencySummary(assetId);
            return { success: response.success !== false, data: response.data };
        } catch (error) {
            return { success: false, data: null, error: this._getErrorMessage(error) };
        }
    }

    async fetchAssets(params = {}) {
        const store = useAssetsStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        if (!tenantId) return;

        store.setLoading(true);
        store.setError(null);

        try {
            const currentPagination = store.pagination;
            const effectiveColumnFilters =
                params.columnFilters !== undefined ? params.columnFilters : store.columnFilters;
            const queryParams = {
                page: params.page || currentPagination.currentPage,
                limit: params.pageSize || currentPagination.itemsPerPage,
                pageSize: params.pageSize || currentPagination.itemsPerPage,
                search: params.search !== undefined ? params.search : store.search,
                sortBy: params.sortBy || store.sorting.sortBy,
                sortOrder: params.sortDirection || params.sortOrder || store.sorting.sortDirection,
                tenantId,
                ...mapColumnFiltersForQuery(effectiveColumnFilters),
            };

            if (params.excludeIds) {
                queryParams.excludeIds = params.excludeIds;
            }

            if (params.maintenanceFocus) {
                queryParams.maintenanceFocus = params.maintenanceFocus;
            }

            if (import.meta.env.DEV) {
                queryParams.debugAssetVisibility = '1';
            }

            const response = await this.service.getAll(queryParams);

            if (import.meta.env.DEV) {
                const paginationData = response.meta || response.pagination || {};
                Logger.debug('[SGRC][assets] fetchAssets', {
                    assetVisibilityMeta: response.assetVisibilityMeta,
                    rowCount: (response.data || []).length,
                    total: paginationData.total,
                    page: paginationData.page,
                });
            }

            const pendingRaw = response.pendingCreateRows || [];
            const mappedPending = pendingRaw.map((item) => ({
                ...item,
                id: item.pendingChangeId,
                _rowSource: 'pendingCreate',
                assetName: item.assetName || item.resourceName || item.title || '-',
                assetId: item.assetId || null,
            }));
            store.setData([...mappedPending, ...(response.data || [])]);

            // Handle both pagination formats from base controller
            const paginationData = response.meta || response.pagination || {};
            const currentPage = parseInt(paginationData.page || paginationData.currentPage || 1);
            const itemsPerPage = parseInt(paginationData.limit || paginationData.pageSize || 10);
            const totalItems = readAssetPaginationTotal(paginationData);
            const totalPages = parseInt(paginationData.pages || paginationData.totalPages || Math.ceil(totalItems / itemsPerPage) || 0);

            store.setPagination({
                currentPage,
                itemsPerPage,
                totalItems,
                totalPages,
                hasNextPage: currentPage < totalPages,
                hasPreviousPage: currentPage > 1
            });

            // Store unique values for column filters
            if (response.uniqueValues) {
                store.setUniqueValues(response.uniqueValues);
            }

            return response;
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            store.setError(errorMessage);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    async createFilteredReportViewerSession(payload) {
        try {
            const response = await this.service.createFilteredReportViewerSession(payload);
            return { success: true, data: response?.data ?? response };
        } catch {
            const message = i18next.t('assets:reports.viewerError', {
                defaultValue: 'Asset report could not be opened. Please try again.',
            });
            toast.error(message);
            return { success: false, error: message };
        }
    }


    async exportFilteredReport(payload) {
        try {
            const blob = await this.service.exportFilteredReport(payload);
            const fileName = i18next.t('assets:reports.fileName', { lng: payload.locale });
            downloadBlob(blob, fileName);
            toast.success(i18next.t('assets:reports.exportSuccess', {
                defaultValue: 'Asset report exported',
            }));
            return { success: true };
        } catch {
            const message = i18next.t('assets:reports.exportError', {
                defaultValue: 'Asset report could not be exported. Please try again.',
            });
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async getAsset(id) {
        const store = useAssetsStore.getState();
        store.setLoading(true);

        try {
            const response = await this.service.getById(id);
            store.setSelectedItem(response.data);
            return response;
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    _isPendingApproval(response) {
        return response?.data?.pendingChangeId || response?.pendingChangeId;
    }

    _isDraftSaved(response) {
        if (response?.draftSaved === true || response?.data?.draftSaved === true) return true;
        const status = response?.approvalStatus || response?.data?.approvalStatus;
        return status === 'draft' && (response?.pendingChangeId || response?.data?.pendingChangeId);
    }

    _linkMutationResult(response) {
        if (this._isDraftSaved(response)) {
            toast.success(
                i18next.t('assets:messages.draftSaved', {
                        defaultValue: 'Change saved as draft',
                    }),
            );
            return {
                success: true,
                draftSaved: true,
                merged: !!response.merged,
                pendingChangeId: response.pendingChangeId || response.data?.pendingChangeId,
            };
        }
        if (this._isPendingApproval(response)) {
            toast.info(
                i18next.t('assets:messages.submitPendingSuccess', {
                        defaultValue: 'Change submitted to approval workflow',
                    }),
            );
            return {
                success: true,
                pendingApproval: true,
                merged: !!response.merged,
                pendingChangeId: response.pendingChangeId || response.data?.pendingChangeId,
            };
        }
        return null;
    }

    /**
     * Live link/unlink result: staged (draft / pending) -> that result, applied -> null.
     * The server broadcasts `asset` on apply; the calling card refetches itself via
     * `afterMutation()` (no local version bump).
     */
    _afterLiveAssetLinkMutation(response) {
        return this._linkMutationResult(response);
    }

    async stageAssetLink(assetId, delta) {
        if (!assetId) return { success: false, error: 'Asset id required' };
        try {
            const response = await this.service.stageAssetLink(assetId, delta);
            if (response.gated === false) {
                if (response.applied) {
                    return { success: true, gated: false, applied: true };
                }
                return { success: false, gated: false };
            }
            const staged = this._linkMutationResult(response);
            if (staged) return staged;
            return { success: response.success !== false, data: response.data };
        } catch (error) {
            const status = error.statusCode ?? error.response?.status;
            if (status === 400) {
                return { success: false, gated: false };
            }
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        }
    }

    async submitPendingUpdate(assetId) {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId || !assetId) {
            return { success: false, error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }
        try {
            const response = await this.service.submitPendingUpdate(assetId);
            toast.success(
                i18next.t('assets:messages.submitPendingSuccess', {
                        defaultValue: 'Change submitted to approval workflow',
                    }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        }
    }

    async fetchPendingUpdateSummary(assetIds) {
        if (!assetIds?.length) return { success: true, data: {} };
        try {
            return await this.service.getPendingUpdateSummary(assetIds);
        } catch {
            return { success: true, data: {} };
        }
    }

    async fetchPendingDraftById(draftId) {
        if (!draftId) return { success: false, data: null };
        try {
            const response = await this.service.getPendingUpdateById(draftId);
            if (!response?.data) return { success: false, data: null };
            return { success: true, data: response.data };
        } catch (error) {
            return { success: false, error: this._getErrorMessage(error) };
        }
    }

    async fetchPendingDraftsForAsset(assetId) {
        if (!assetId) return { success: true, data: [] };
        try {
            const response = await this.service.getPendingUpdate(assetId);
            return { success: true, data: response?.data || [] };
        } catch {
            return { success: true, data: [] };
        }
    }

    async fetchWorkflowApprovalDetail(assetId) {
        if (!assetId) return { success: false, error: 'Asset id required' };
        try {
            return await this.service.getWorkflowApprovalDetail(assetId);
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            return { success: false, error: errorMessage };
        }
    }

    async createAsset(data) {
        const store = useAssetsStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        try {
            const response = await this.service.create({
                ...data,
                tenantId
            });

            if (this._isPendingApproval(response)) {
                toast.info(
                    i18next.t('assets:messages.submitPendingSuccess', {
                        defaultValue: 'Change submitted to approval workflow',
                    }),
                );
                store.closeForm();
                this._invalidateDropdownCache();
                return {
                    success: true,
                    pendingApproval: true,
                    merged: !!response.merged,
                    pendingChangeId: response.data?.pendingChangeId || response.pendingChangeId
                };
            }

            toast.success(i18next.t('assets:messages.createSuccess', {
                defaultValue: 'Asset created successfully'
            }));

            this._invalidateDropdownCache();
            store.closeForm();

            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * @param {string} id
     * @param {Object} data
     * @param {{ skipCloseForm?: boolean }} [options] - Use skipCloseForm on asset detail (inline edit) so the list modal is not closed.
     */
    async updateAsset(id, data, options = {}) {
        const { skipCloseForm = false, silentSuccess = false } = options;
        const store = useAssetsStore.getState();
        store.setLoading(true);

        try {
            const response = await this.service.update(id, data);

            if (this._isDraftSaved(response)) {
                toast.success(
                    i18next.t('assets:messages.draftSaved', {
                            defaultValue: 'Change saved as draft',
                        }),
                );
                if (!skipCloseForm) store.closeForm();
                this._invalidateDropdownCache();
                return {
                    success: true,
                    draftSaved: true,
                    merged: !!response.merged,
                    pendingChangeId: response.data?.pendingChangeId || response.pendingChangeId,
                };
            }

            if (this._isPendingApproval(response)) {
                toast.info(
                    i18next.t('assets:messages.submitPendingSuccess', {
                        defaultValue: 'Change submitted to approval workflow',
                    }),
                );
                if (!skipCloseForm) store.closeForm();
                this._invalidateDropdownCache();
                return {
                    success: true,
                    pendingApproval: true,
                    merged: !!response.merged,
                    pendingChangeId: response.data?.pendingChangeId || response.pendingChangeId,
                };
            }

            if (!silentSuccess) {
                toast.success(i18next.t('assets:messages.updateSuccess', {
                    defaultValue: 'Asset updated successfully'
                }));
            }

            this._invalidateDropdownCache();
            if (!skipCloseForm) store.closeForm();

            if (response.data) {
                store.setCurrentAsset(response.data);
            }

            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    async deleteAsset(id) {
        const store = useAssetsStore.getState();
        store.setLoading(true);

        try {
            const response = await this.service.delete(id);

            if (this._isPendingApproval(response)) {
                toast.info(
                    i18next.t('assets:messages.deletePendingApproval', {
                        defaultValue: 'Delete request submitted to approval workflow',
                    }),
                );
                this._invalidateDropdownCache();
                return { success: true, pendingApproval: true, merged: !!response.merged };
            }

            toast.success(i18next.t('assets:messages.deleteSuccess', {
                defaultValue: 'Asset deleted'
            }));

            this._invalidateDropdownCache();

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    async bulkDeleteAssets(ids) {
        const store = useAssetsStore.getState();
        store.setLoading(true);

        try {
            const results = await Promise.all(ids.map(async (id) => {
                try {
                    const response = await this.service.delete(id);
                    if (this._isPendingApproval(response)) {
                        return { success: true, pendingApproval: true, id };
                    }
                    return { success: true, pendingApproval: false, id };
                } catch (error) {
                    return { success: false, error: this._getErrorMessage(error), id };
                }
            }));

            const deletedCount = results.filter((r) => r.success && !r.pendingApproval).length;
            const pendingCount = results.filter((r) => r.success && r.pendingApproval).length;
            const failedCount = results.filter((r) => !r.success).length;

            if (deletedCount > 0) {
                toast.success(i18next.t('assets:messages.bulkDeleteSuccess', {
                    count: deletedCount,
                    defaultValue: '{{count}} records deleted'
                }));
            }

            if (pendingCount > 0) {
                toast.info(i18next.t('assets:messages.bulkDeletePendingApproval', { count: pendingCount }));
            }

            if (failedCount > 0) {
                toast.error(i18next.t('assets:messages.bulkDeletePartialFailure', {
                    count: failedCount,
                    defaultValue: '{{count}} record(s) could not be deleted.',
                }));
            }

            this._invalidateDropdownCache();

            return {
                success: failedCount === 0,
                deletedCount,
                pendingCount,
                failedCount
            };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    async fetchStats() {
        const store = useAssetsStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        if (!tenantId) {
            store.setStats(null);
            return { success: false };
        }

        try {
            const response = await this.service.getStatistics();
            let payload = response.success && response.data ? response.data : null;
            const normalized = payload ? this._normalizeAssetStatsPayload(payload) : null;

            if (normalized) {
                store.setStats(normalized);
            } else {
                store.setStats(null);
            }
            return { success: Boolean(normalized), data: normalized };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            store.setStats(null);
            return { success: false, error: errorMessage };
        }
    }

    /**
     * Full relationship map for assets list graph tab -- does not mutate list store.
     */
    async fetchRelationshipMap(params = {}) {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId) {
            return { success: false, data: { nodes: [], edges: [] }, error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }
        try {
            const response = await this.service.getRelationshipMap({
                tenantId,
                ...params,
            });
            const raw = response.data || {};
            const data = Array.isArray(raw.nodes) ? raw : (raw.data || raw);
            return {
                success: response.success !== false,
                data: {
                    nodes: data.nodes || [],
                    edges: data.edges || [],
                    truncated: !!data.truncated,
                    limit: data.limit,
                    totalNodes: data.totalNodes,
                    totalEdges: data.totalEdges,
                },
            };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage, data: { nodes: [], edges: [] } };
        }
    }

    /**
     * Loads only the main asset list for Process form linking (no unit/user/vendor fan-out).
     */
    async fetchProcessLinkedAssetsIfNeeded() {
        const store = useAssetsStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId) return { success: false };

        const existing = store.dropdownData?.assets;
        const cachedTenant = store.dropdownData?._tenantId;
        if (existing && existing.length > 0 && cachedTenant === tenantId) {
            return { success: true, data: store.dropdownData };
        }

        try {
            const response = await this.service.getAll({
                limit: 1000,
                tenantId,
                ...ASSET_LIST_PICKER_QUERY,
            });
            const assets = response.data || [];
            store.setDropdownData({
                ...(store.dropdownData || {}),
                assets,
                _tenantId: tenantId,
            });
            return { success: true, data: store.dropdownData };
        } catch (error) {
            Logger.error('Failed to fetch process asset options:', { error: error?.message });
            return { success: false };
        }
    }

    async fetchFilterOptionData({ force = false } = {}) {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId) {
            return { success: false, error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }

        const store = useAssetsStore.getState();
        const dropdownData = store.dropdownData || {};
        if (!force && dropdownData._tenantId === tenantId && this._dropdownPickerDataReady(dropdownData)) {
            return { success: true, data: dropdownData };
        }

        try {
            const refState = await this._ensureDropdownReferenceEntities(tenantId);
            const entities = refState.entities || {};
            const [assetGroups, classificationRows] = await Promise.all([
                this._resolveReferenceDropdownRows('assetGroup'),
                this._resolveReferenceDropdownRows('classification'),
            ]);
            const classificationLevels = this._mapClassificationLevels(
                classificationRows.length > 0 ? classificationRows : entities.classification,
            );

            const nextDropdownData = {
                ...dropdownData,
                assetGroups: assetGroups.length > 0 ? assetGroups : (entities.assetGroup || []),
                assetInventoryCategories: entities.assetCategory || [],
                classificationLevels,
                owners: buildOwnerGroupedOptions({
                    users: entities.user || [],
                    teams: entities.team || [],
                    units: entities.unit || [],
                    roles: entities.role || [],
                }),
                _tenantId: tenantId,
            };

            store.setDropdownData(nextDropdownData);
            return { success: true, data: nextDropdownData };
        } catch (error) {
            return { success: false, error: this._getErrorMessage(error) };
        }
    }

    /**
     * @param {{ force?: boolean }} [options] -- force: bypass TTL + in-flight coalescing (run a fresh network fetch after any current one finishes)
     */
    async fetchDropdownData(options = {}) {
        const { force = false } = options;
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId) {
            return { success: false };
        }

        if (force && this._assetDropdownInflight) {
            await this._assetDropdownInflight.catch(() => {});
            this._assetDropdownInflight = null;
        }

        if (!force) {
            const store = useAssetsStore.getState();
            if (this._isDropdownCacheWarm(store.dropdownData, tenantId)
                && this._dropdownPickerDataReady(store.dropdownData)) {
                return { success: true, data: store.dropdownData };
            }
            if (this._isDropdownCacheWarm(store.dropdownData, tenantId)
                && !this._dropdownPickerDataReady(store.dropdownData)) {
                return this.fetchFilterOptionData({ force: true });
            }
            if (this._assetDropdownInflight) {
                return this._assetDropdownInflight;
            }
        }

        const run = this._executeFetchDropdownData(tenantId)
            .then((result) => {
                if (result?.success) {
                    this._assetDropdownLastSuccessAt = Date.now();
                }
                return result;
            })
            .finally(() => {
                if (this._assetDropdownInflight === run) {
                    this._assetDropdownInflight = null;
                }
            });

        this._assetDropdownInflight = run;
        return run;
    }

    async _executeFetchDropdownData(tenantId) {
        const store = useAssetsStore.getState();

        try {
            const refState = await this._ensureDropdownReferenceEntities(tenantId);
            const entities = refState.entities || {};
            const users = entities.user || [];
            const teams = entities.team || [];
            const roles = entities.role || [];
            const [assetGroups, classificationRows] = await Promise.all([
                this._resolveReferenceDropdownRows('assetGroup'),
                this._resolveReferenceDropdownRows('classification'),
            ]);
            const classificationLevels = this._mapClassificationLevels(
                classificationRows.length > 0 ? classificationRows : entities.classification,
            );

            // The only network request here: asset picker rows (parent / location / infrastructure).
            const assetsRes = await this.service.getAll({
                limit: 1000,
                tenantId,
                ...ASSET_LIST_PICKER_QUERY,
            });
            const pickerRows = splitAssetPickerRows(assetsRes.data || []);

            store.setDropdownData({
                assets: pickerRows.assets,
                locationAssets: pickerRows.locationAssets,
                infrastructureAssets: pickerRows.infrastructureAssets,
                // Reference cache rows (`registry.assetCategory.fields` carries CIA + group).
                assetInventoryCategories: entities.assetCategory || [],
                units: entities.unit || [],
                assetGroups: assetGroups.length > 0 ? assetGroups : (entities.assetGroup || []),
                users,
                vendors: entities.vendor || [],
                classificationLevels,
                securityValues: [
                    { id: 1, name: '1 - ' + i18next.t('assets:form.fields.securityValues.options.veryLow') },
                    { id: 2, name: '2 - ' + i18next.t('assets:form.fields.securityValues.options.low') },
                    { id: 3, name: '3 - ' + i18next.t('assets:form.fields.securityValues.options.medium') },
                    { id: 4, name: '4 - ' + i18next.t('assets:form.fields.securityValues.options.high') },
                    { id: 5, name: '5 - ' + i18next.t('assets:form.fields.securityValues.options.veryHigh') },
                ],
                owners: buildOwnerGroupedOptions({
                    users,
                    teams,
                    units: entities.unit || [],
                    roles,
                }),
                ticketSLAs: entities.ticketSLA || [],
                _tenantId: tenantId,
            });
            return { success: true, data: store.dropdownData };
        } catch (error) {
            Logger.error('Failed to fetch dropdown data:', { error: error?.message });
            return { success: false };
        }
    }

    async fetchRelationships(id) {
        try {
            const response = await this.service.getRelationships(id);
            return response;
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        }
    }

    async addRelationship(id, data) {
        try {
            const response = await this.service.createRelationships(id, data);
            toast.success(i18next.t('assets:messages.relationshipAdded', {
                defaultValue: 'Relationship added',
            }));
            return response;
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        }
    }

    async removeRelationship(id, data) {
        try {
            const response = await this.service.deleteRelationships(id, data);
            toast.success(i18next.t('assets:messages.relationshipRemoved', {
                defaultValue: 'Relationship removed',
            }));
            return response;
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        }
    }

    async getRelatedRisks(id, params) {
        try {
            return await this.service.getRelatedRisks(id, params);
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        }
    }

    async addRisks(id, riskIds) {
        try {
            const response = await this.service.addRisks(id, riskIds);
            const staged = this._afterLiveAssetLinkMutation(response);
            if (staged) return staged;

            toast.success(i18next.t('assets:messages.risksAdded', {
                defaultValue: 'Risks linked',
            }));
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        }
    }

    async removeRisk(id, riskId) {
        try {
            const response = await this.service.removeRisk(id, riskId);
            const staged = this._afterLiveAssetLinkMutation(response);
            if (staged) return staged;

            toast.success(i18next.t('assets:messages.riskRemoved', {
                defaultValue: 'Risk unlinked',
            }));
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        }
    }

    async removeRisksBulk(id, riskIds) {
        try {
            const response = await this.service.removeRisksBulk(id, riskIds);
            const staged = this._afterLiveAssetLinkMutation(response);
            if (staged) return staged;

            toast.success(i18next.t('assets:messages.multiUnlinked', {
                count: riskIds.length,
                defaultValue: '{{count}} risks unlinked',
            }));
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        }
    }

    async getRelatedThreats(id, params) {
        try {
            return await this.service.getRelatedThreats(id, params);
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        }
    }

    async addThreats(id, threatIds) {
        try {
            const response = await this.service.addThreats(id, threatIds);
            const staged = this._afterLiveAssetLinkMutation(response);
            if (staged) return staged;
            toast.success(i18next.t('assets:messages.threatsAdded', {
                defaultValue: 'Threats linked',
            }));
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        }
    }

    async removeThreat(id, threatId) {
        try {
            const response = await this.service.removeThreat(id, threatId);
            const staged = this._afterLiveAssetLinkMutation(response);
            if (staged) return staged;
            toast.success(i18next.t('assets:messages.threatRemoved', {
                defaultValue: 'Threat unlinked',
            }));
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        }
    }

    async removeThreatsBulk(id, threatIds) {
        try {
            const response = await this.service.removeThreatsBulk(id, threatIds);
            const staged = this._afterLiveAssetLinkMutation(response);
            if (staged) return staged;
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        }
    }

    async getrelatedControls(id, params) {
        try {
            return await this.service.getrelatedControls(id, params);
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        }
    }

    async addScfControls(id, scfControlIds) {
        try {
            const response = await this.service.addScfControls(id, scfControlIds);
            const staged = this._afterLiveAssetLinkMutation(response);
            if (staged) return staged;
            toast.success(i18next.t('assets:messages.controlsAdded', {
                defaultValue: 'Controls linked',
            }));
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        }
    }

    async removeScfControl(id, scfControlId) {
        try {
            const response = await this.service.removeScfControl(id, scfControlId);
            const staged = this._afterLiveAssetLinkMutation(response);
            if (staged) return staged;
            toast.success(i18next.t('assets:messages.controlRemoved', {
                defaultValue: 'Control unlinked',
            }));
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        }
    }

    async removeScfControlsBulk(id, scfControlIds) {
        try {
            const response = await this.service.removeScfControlsBulk(id, scfControlIds);
            const staged = this._afterLiveAssetLinkMutation(response);
            if (staged) return staged;
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        }
    }

    notifyPendingApprovalLoadFailed() {
        toast.error(
            i18next.t('assets:table.pendingApproval.loadFailed', {
                defaultValue: 'Could not load pending approval record',
            }),
        );
    }

    notifyPendingApprovalNone() {
        toast.error(
            i18next.t('assets:table.pendingApproval.none', {
                defaultValue: 'Pending approval record not found',
            }),
        );
    }

    notifyPendingApprovalNoUpdate() {
        toast.error(
            i18next.t('assets:table.pendingApproval.noUpdatePending', {
                defaultValue: 'No pending update record',
            }),
        );
    }

    notifyProcessesLinked(count) {
        toast.success(
            i18next.t('assets:messages.processesLinked', { count, defaultValue: '{{count}} processes linked' }),
        );
    }

    notifyProcessUnlinked() {
        toast.success(i18next.t('assets:messages.processUnlinked', { defaultValue: 'Process link removed' }));
    }

    notifyProcessesUnlinkedBulk(count) {
        toast.success(
            i18next.t('assets:messages.processesUnlinkedBulk', {
                count,
                defaultValue: '{{count}} process links removed',
            }),
        );
    }

    notifyThreatsLinked(count) {
        toast.success(
            i18next.t('assets:messages.threatsLinked', { count, defaultValue: '{{count}} threats linked' }),
        );
    }

    notifyThreatsUnlinkedBulk(count) {
        toast.success(
            i18next.t('assets:messages.threatsUnlinkedBulk', {
                count,
                defaultValue: '{{count}} threat links removed',
            }),
        );
    }

    notifyVendorsLinked(count) {
        toast.success(
            i18next.t('assets:compliancePanel.messages.vendorsLinked', {
                count,
                defaultValue: '{{count}} vendors linked',
            }),
        );
    }

    notifyVendorUnlinked() {
        toast.success(
            i18next.t('assets:compliancePanel.messages.vendorUnlinked', { defaultValue: 'Vendor link removed' }),
        );
    }

    notifyVendorsUnlinkedBulk(count) {
        toast.success(
            i18next.t('assets:compliancePanel.messages.vendorsUnlinkedBulk', {
                count,
                defaultValue: '{{count}} vendor links removed',
            }),
        );
    }

    notifyPdLinked(count) {
        toast.success(
            i18next.t('assets:compliancePanel.messages.pdLinked', {
                count,
                defaultValue: '{{count}} personal data inventories linked',
            }),
        );
    }

    notifyPdUnlinked() {
        toast.success(
            i18next.t('assets:compliancePanel.messages.pdUnlinked', { defaultValue: 'Personal data inventory link removed' }),
        );
    }

    notifyPdUnlinkedBulk(count) {
        toast.success(
            i18next.t('assets:compliancePanel.messages.pdUnlinkedBulk', {
                count,
                defaultValue: '{{count}} personal data inventory links removed',
            }),
        );
    }

    notifyAssociationError() {
        toast.error(i18next.t('common:messages.error', { defaultValue: 'Operation failed' }));
    }

    notifyVulnerabilitiesLinked(count) {
        toast.success(
            i18next.t('assets:messages.vulnerabilitiesLinked', {
                count,
                defaultValue: '{{count}} vulnerabilities linked',
            }),
        );
    }

    notifyLicensesLinked(count) {
        toast.success(
            i18next.t('assets:messages.licensesLinked', {
                count,
                defaultValue: '{{count}} licenses linked to this asset',
            }),
        );
    }

    notifyLicensesUnlinkedBulk(count) {
        toast.success(
            i18next.t('assets:messages.licensesUnlinkedBulk', {
                count,
                defaultValue: 'Asset unlinked from selected licenses',
            }),
        );
    }

    notifyDisposalsLinked(count) {
        toast.success(
            i18next.t('assets:messages.disposalsLinked', {
                count,
                defaultValue: '{{count}} disposal records linked to this asset',
            }),
        );
    }

    notifyDisposalsUnlinkedBulk(count) {
        toast.success(
            i18next.t('assets:messages.disposalsUnlinkedBulk', {
                count,
                defaultValue: 'Asset unlinked from selected disposal records',
            }),
        );
    }
}
